import React, { useState, useEffect } from "react"
import { Grid } from "@ui/common/grids/Grid"
import { FormField, FormFieldDirection, FormFieldNote, FormFieldNoteType } from "@ui/common/form/FormField"
import { TextInput, TextInputType } from "@ui/common/form/TextInput"
import { ServiceItemRequiredFieldKey } from "@model/company/CompanySettings"
import { ServiceItemForm } from "@model/serviceRequests/ServiceItemForm"
import { Asset } from "@model/assets/Asset"
import { stringIsNotEmpty } from "@lib/TypeUtil"
import { WorkOrderServiceItemsReviewModal } from "@ui/workOrders/CompleteWorkOrderModal"

type Props = {
    isVisible: boolean
    onClose: () => void
    onConfirm: (
        completionDate: Date,
        items: ServiceItemForm[],
        physicalHourMeter: string,
        physicalOdometer: string
    ) => void
    serviceItems: ServiceItemForm[]
    missingFieldsByItem?: Record<number, ServiceItemRequiredFieldKey[]>
    asset: Asset | null
    initialCompletionDate: Date | null
    initialPhysicalHourMeter: string | null
    initialPhysicalOdometer: string | null
    // Latest Smart Hub readings, shown as a reference under the physical meter fields.
    smartHubHourMeter?: string | null
    smartHubOdometer?: string | null
}

// Action Item #6827: popup shown when the Work Order Status is set to Closed.
// Mirrors the Complete Work Order popup and adds the values required for
// closing a work order (physical meters), following the same required rules
// as the work order form (required only when the asset displays the meter).
export function ClosingWorkOrderModal({
    isVisible,
    onClose,
    onConfirm,
    serviceItems,
    missingFieldsByItem,
    asset,
    initialCompletionDate,
    initialPhysicalHourMeter,
    initialPhysicalOdometer,
    smartHubHourMeter,
    smartHubOdometer,
}: Props): JSX.Element {
    const [physicalHourMeter, setPhysicalHourMeter] = useState<string>(initialPhysicalHourMeter ?? "")
    const [physicalOdometer, setPhysicalOdometer] = useState<string>(initialPhysicalOdometer ?? "")

    useEffect(() => {
        if (isVisible) {
            setPhysicalHourMeter(initialPhysicalHourMeter ?? "")
            setPhysicalOdometer(initialPhysicalOdometer ?? "")
        }
    }, [isVisible])

    const hourMeterRequired = asset?.displayHourMeter === true
    const odometerRequired = asset?.displayOdometer === true
    const metersValid =
        (!hourMeterRequired || stringIsNotEmpty(physicalHourMeter)) &&
        (!odometerRequired || stringIsNotEmpty(physicalOdometer))

    return (
        <WorkOrderServiceItemsReviewModal
            isVisible={isVisible}
            onClose={onClose}
            serviceItems={serviceItems}
            missingFieldsByItem={missingFieldsByItem}
            title="Closing Work Order"
            confirmLabel="Ready to Close"
            initialCompletionDate={initialCompletionDate}
            additionalFieldsValid={metersValid}
            additionalFields={
                <Grid columns={2}>
                    <FormField
                        label={hourMeterRequired ? "Physical Hour Meter*" : "Physical Hour Meter"}
                        direction={FormFieldDirection.Column}
                    >
                        <TextInput
                            type={TextInputType.Decimal}
                            min={0}
                            value={physicalHourMeter}
                            onChange={(value) => setPhysicalHourMeter(value)}
                        />
                        {asset?.displayHourMeter && smartHubHourMeter != null && (
                            <FormFieldNote>
                                Smart Hub Hour Meter: {smartHubHourMeter}
                            </FormFieldNote>
                        )}
                        {hourMeterRequired && !stringIsNotEmpty(physicalHourMeter) && (
                            <FormFieldNote type={FormFieldNoteType.Error}>
                                Physical Hour Meter required
                            </FormFieldNote>
                        )}
                    </FormField>
                    <FormField
                        label={odometerRequired ? "Physical Odometer*" : "Physical Odometer"}
                        direction={FormFieldDirection.Column}
                    >
                        <TextInput
                            type={TextInputType.Integer}
                            min={0}
                            value={physicalOdometer}
                            onChange={(value) => setPhysicalOdometer(value)}
                        />
                        {asset?.displayOdometer && smartHubOdometer != null && (
                            <FormFieldNote>
                                Smart Hub Odometer: {smartHubOdometer}
                            </FormFieldNote>
                        )}
                        {odometerRequired && !stringIsNotEmpty(physicalOdometer) && (
                            <FormFieldNote type={FormFieldNoteType.Error}>
                                Physical Odometer required
                            </FormFieldNote>
                        )}
                    </FormField>
                </Grid>
            }
            onConfirm={(completionDate, items) =>
                onConfirm(completionDate, items, physicalHourMeter, physicalOdometer)
            }
        />
    )
}
